import { Component, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApiService } from '@core/services/api.service';
import { AuthService } from '@core/services/auth.service';
import { POLICY_LIST, POLICY_VERSION } from '@core/models/policies';

@Component({
  standalone: true,
  selector: 'app-policy-consent',
  imports: [CommonModule],
  template: `
    <div class="pc-overlay" *ngIf="open">
      <div class="pc-modal">
        <div class="pc-head">
          <div class="pc-head-ic"><span class="material-icons">verified_user</span></div>
          <div>
            <h2>Accept Company Policies</h2>
            <p>Please review and accept the following policies to continue using {{ appName }}.</p>
          </div>
        </div>

        <div class="pc-body">
          <div class="pc-policy" *ngFor="let r of rules">
            <span class="material-icons pc-policy-ic">check_circle</span>
            <div><div class="pc-policy-title">{{ r.title }}</div><div class="pc-policy-text">{{ r.detail }}</div></div>
          </div>
        </div>

        <label class="pc-accept">
          <input type="checkbox" [checked]="accepted" (change)="accepted = $any($event.target).checked">
          <span>I have read and accept all the above policies.</span>
        </label>

        <div class="pc-foot">
          <button class="pc-btn" [disabled]="!accepted || saving" (click)="accept()">
            {{ saving ? 'Saving…' : 'Accept & Continue' }}
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .pc-overlay { position: fixed; inset: 0; z-index: 3000; background: rgba(20,10,12,.5);
      display: grid; place-items: center; backdrop-filter: blur(3px); }
    .pc-modal { width: min(560px, 94vw); max-height: 88vh; display: flex; flex-direction: column;
      background: #fff; border-radius: 16px; box-shadow: 0 24px 60px rgba(20,10,12,.35); overflow: hidden; }
    .pc-head { display: flex; gap: 14px; align-items: center; padding: 22px 24px;
      background: linear-gradient(135deg,#334155,#885200); color: #fff; }
    .pc-head h2 { font-size: 18px; font-weight: 800; margin: 0; }
    .pc-head p { font-size: 12.5px; opacity: .9; margin: 3px 0 0; }
    .pc-head-ic { width: 44px; height: 44px; border-radius: 12px; background: rgba(255,255,255,.18);
      display: grid; place-items: center; flex-shrink: 0; .material-icons { font-size: 24px; } }
    .pc-body { padding: 18px 24px; overflow-y: auto; display: flex; flex-direction: column; gap: 12px; }
    .pc-policy { display: flex; gap: 11px; }
    .pc-policy-ic { color: #1f7a4d; font-size: 19px; flex-shrink: 0; margin-top: 1px; }
    .pc-policy-title { font-size: 14px; font-weight: 700; color: #2a2024; }
    .pc-policy-text { font-size: 12.5px; color: #6b5f63; line-height: 1.5; }
    .pc-accept { display: flex; align-items: center; gap: 10px; padding: 14px 24px;
      border-top: 1px solid #eee; font-size: 13.5px; font-weight: 600; color: #2a2024; cursor: pointer;
      input { width: 17px; height: 17px; accent-color: #334155; } }
    .pc-foot { padding: 0 24px 20px; }
    .pc-btn { width: 100%; height: 44px; border: 0; border-radius: 10px; background: #334155; color: #fff;
      font-size: 14px; font-weight: 700; cursor: pointer; &:hover:not(:disabled) { background: #50000a; }
      &:disabled { opacity: .5; cursor: not-allowed; } }
  `],
})
export class PolicyConsentComponent {
  private destroyRef = inject(DestroyRef);
  appName = 'NovarcPulse';
  policies = POLICY_LIST;
  rules: { title: string; detail: string }[] = [];
  open = false;
  saving = false;
  accepted = false;
  private checked = false;

  private buildRules(p: any): { title: string; detail: string }[] {
    p = p || {};
    return [
      { title: 'Attendance Regularisation', detail: `You can regularise up to ${p.regMaxDaysBack ?? 7} day(s) in the past, max ${p.regMaxPerMonth ?? 5} request(s) per month.` },
      { title: 'Grace Period', detail: `${p.graceMinutes ?? 10} minute(s) after shift start.${p.lateLoginHalfDay ? ' A late login is counted as half-day.' : ''}` },
      { title: 'Tardiness', detail: `${p.consecutiveLateLimit ?? 3} consecutive late logins trigger a tardiness notice.` },
      { title: 'Working Hours', detail: `${p.fullDayHours ?? 8}h = full day, ${p.halfDayHours ?? 4}h = half day.` },
      { title: 'Off-Location Attendance', detail: `You may mark attendance from a non-allocated location up to ${p.offLocationDaysPerMonth ?? 2} day(s) per month${p.offLocationRequiresApproval ? ' (manager approval required)' : ''}.` },
      { title: 'Leave', detail: `Apply at least ${p.minLeaveNoticeDays ?? 2} day(s) in advance. Entitlement: ${p.casualLeavePerYear ?? 12} casual, ${p.sickLeavePerYear ?? 6} sick, ${p.earnedLeavePerYear ?? 15} earned per year.` },
      { title: 'Conduct & Data Privacy', detail: 'Abide by the code of conduct and protect confidential information (DPDP).' },
    ];
  }

  constructor(private router: Router, private auth: AuthService, private api: ApiService) {
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(e => this.maybeCheck(e.urlAfterRedirects || e.url));
  }

  private maybeCheck(url: string): void {
    if (this.checked) return;
    const path = (url || '').split('?')[0];
    if (path.includes('/login') || path.includes('/onboard')) return;
    if (!this.auth.getToken()) return;
    this.checked = true;
    this.api.get('/policy-acknowledgements/me').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (latest: any) => {
        if (!latest || latest.version !== POLICY_VERSION) {
          this.api.get('/settings/attendance-policy').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (p: any) => { this.rules = this.buildRules(p); this.open = true; },
            error: () => { this.rules = this.buildRules(null); this.open = true; },
          });
        }
      },
      error: () => {},
    });
  }

  accept(): void {
    if (!this.accepted) return;
    this.saving = true;
    this.api.post('/policy-acknowledgements', {
      version: POLICY_VERSION,
      policies: this.rules.map(r => ({ key: r.title, title: r.title, detail: r.detail })),
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => { this.saving = false; this.open = false; },
      error: () => { this.saving = false; this.open = false; },
    });
  }
}
